import React, { useCallback } from 'react';

import { ClickToCallAgentClosedInputDto } from '@api/ecommerce-xp';
import { CoreHelper, ValidationHelper } from '@bcp-channels-sharedcomponents-reactjs/core/dist/$helpers';
import { useDidMount, useTranslation } from '@bcp-channels-sharedcomponents-reactjs/core/dist/hooks';
import { useForm } from '@bcp-channels-sharedcomponents-reactjs/form-utils/dist/$components';
import { FormHelper } from '@bcp-channels-sharedcomponents-reactjs/form-utils/dist/helpers';
import { CalendarHorizontalRef, PopupMenuItem, PopupMenuRef } from '@bcp-nextgen-dx-component-factory/design-system';
import { ActiveItem } from '@bcp-nextgen-dx-component-factory/design-system';
import { DatePickerRef } from '@bcp-nextgen-dx-component-factory/design-system';
import { useBuildPhoneCodes, useReactiveForm, UseReactiveFormInputPropsOutput } from '@hooks';
import { useNavigation } from '@navigation';
import { useFetchClickToCallAgentClosed } from '@services';
import { useServiceResponse } from '@store';
import { Anything } from '@types';

import { RequestToCallCloseProps } from './requestToCallClose';

interface RequestToCallCloseResources {
	title: string;
	subtitle: string;
	dateLabel: string;
	timeLabel: string;
	titleForm: string;
	inputLabelContact: string;
	inputLabelEmail: string;
	inputLabelName: string;
	inputCountry: string;
	linkBack: string;
	sendRequest: string;
	required: string;
	invalidEmail: string;
	invalidPhone: string;
	invalidDate: string;
	indicativeGlobal: string;
}

type DateStatus = 'default' | 'error';

export const useRequestToCallCloseHelper = ({ hideDisclaimer }: RequestToCallCloseProps) => {
	const t = useTranslation();
	const { back } = useNavigation();
	const { fetch: fetchClickToCallAgentClosed, success: agentClosedSuccess } = useFetchClickToCallAgentClosed();
	const { clickToCallIsOpen } = useServiceResponse();
	const { phoneCodes, initialPhoneId, getSelectedPhone } = useBuildPhoneCodes({ withFlags: false });
	const [statusDate, setStatusDate] = React.useState<DateStatus>('default');
	const [messageErrorDate, setMessageErrorDate] = React.useState<string | undefined>(undefined);

	const popupMenuIndicativeRef = React.useRef<PopupMenuRef>(null);
	const popupMenuTimeRef = React.useRef<PopupMenuRef>(null);

	const datePickerRef = React.useRef<DatePickerRef>(null);
	const calendarRef = React.useRef<CalendarHorizontalRef>(null);

	useDidMount(() => {
		popupMenuTimeRef.current?.setActiveItem(0);
		popupMenuIndicativeRef.current?.setActiveItem(0);
		if (initialPhoneId !== undefined) {
			popupMenuIndicativeRef.current?.setActiveItem(initialPhoneId);
			const selectedPhone = getSelectedPhone(phoneCodes[initialPhoneId].content as string);
			setValues({
				indicative: selectedPhone?.internationalCode as string,
				country: selectedPhone?.countryPT as string,
				timeSchedule: '08:00 - 10:00',
				date: new Date(),
			});
		}
	});

	const timeCodes = React.useMemo(() => {
		const getTimeSchedule = !CoreHelper.isNullOrEmpty(clickToCallIsOpen?.schedule)
			? (clickToCallIsOpen?.schedule as string[])
			: [];
		const result: PopupMenuItem[] = getTimeSchedule.map((time) => {
			return {
				content: `${time}`,
			};
		});
		return result;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [clickToCallIsOpen?.schedule?.toString()]);

	const resources: RequestToCallCloseResources = {
		title: t('placeholders.requestToCall.close.title'),
		subtitle: t('placeholders.requestToCall.close.subtitle'),
		dateLabel: t('placeholders.requestToCall.form.date'),
		timeLabel: t('placeholders.requestToCall.form.time'),
		titleForm: t('placeholders.requestToCall.form.title'),
		inputLabelContact: t('placeholders.requestToCall.form.phone'),
		inputLabelEmail: t('general.email'),
		inputLabelName: t('placeholders.requestToCall.form.name'),
		inputCountry: t('placeholders.requestToCall.form.country'),
		linkBack: t('general.back'),
		sendRequest: t('placeholders.requestToCall.form.sendRequest'),
		required: t('general.errors.required'),
		invalidEmail: t('general.errors.invalidEmail'),
		invalidPhone: t('general.errors.invalidPhone'),
		invalidDate: t('general.errors.invalidDate'),
		indicativeGlobal: `+${t('global.defaultInternationalDialing')}`,
	};

	const handlePressIndicative = useCallback((item: ActiveItem | undefined) => {
		if (item?.content !== undefined) {
			const selectedPhone = getSelectedPhone(item.content);
			if (selectedPhone !== undefined && selectedPhone.internationalCode !== undefined) {
				handleOnChangeField(['indicative', 'phoneNumber', 'country'])([
					selectedPhone.internationalCode as string,
					'',
					selectedPhone.countryPT as string,
				]);
			}
		}
		popupMenuIndicativeRef.current?.hide();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handlePressTime = useCallback((item: ActiveItem | undefined) => {
		if (item?.content !== undefined) {
			const time = item?.content;
			handleOnChangeField('timeSchedule')(time);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleOnSubmit = useCallback((values: Anything) => {
		const phone = `+${values.indicative}${values.phoneNumber}`;
		const dateFormat = values.date?.toLocaleDateString('en-GB');
		const data: ClickToCallAgentClosedInputDto = {
			email: values.email,
			phoneNumber: phone,
			callDate: dateFormat,
			name: values.name,
			phoneCountryCode: values.country,
			callHour: values.timeSchedule,
		};
		fetchClickToCallAgentClosed(data);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const clearInput = (inputProps: UseReactiveFormInputPropsOutput) => {
		inputProps.onChangeValue('');
		inputProps.onBlur(true);
	};
	const form = useForm('requestToCall');
	const { isValid, getInputProps, handleOnChangeField, setValues } = useReactiveForm({
		name: form.name,
		onSubmit: handleOnSubmit,
		validate: (values: Anything) => {
			const isPT = values.indicative !== undefined && values.indicative === resources.indicativeGlobal;
			return {
				name: FormHelper.validateField(values.name, [ValidationHelper.validateRequired(resources.required)]),
				email: FormHelper.validateField(values.email, [
					ValidationHelper.validateRequired(resources.required),
					ValidationHelper.validateEmail(resources.invalidEmail),
				]),
				phoneNumber: FormHelper.validateField(values.phoneNumber, [
					ValidationHelper.validateRequired(resources.required),
					isPT ? ValidationHelper.validateEqualLength(9, resources.invalidPhone) : () => undefined,
				]),
				country: FormHelper.validateField(values.country, []),
			};
		},
	});

	const onInvalidDate = () => {
		setStatusDate('error');
		handleOnChangeField('date')(undefined);
		setMessageErrorDate(resources.invalidDate);
	};
	const disableDatesFn = React.useCallback((date: Date): boolean => date.getDay() === 6 || date.getDay() === 0, []);

	const onDateChange = (date?: Date) => {
		if (date) {
			handleOnChangeField('date')(date);
			if (statusDate === 'error') {
				setStatusDate('default');
				setMessageErrorDate(undefined);
			}
		} else {
			setStatusDate('error');
			handleOnChangeField('date')(undefined);
			setMessageErrorDate(resources.required);
		}
	};

	const isDisabled = React.useMemo(() => {
		return !isValid || (isValid && statusDate === 'error' && !CoreHelper.isNullOrEmpty(messageErrorDate));
	}, [isValid, statusDate, messageErrorDate]);

	const onPressSendRequest = () => {
		form.submit();
		hideDisclaimer();
	};
	// const newDate = new Date();
	const newDateToCompare = new Date(new Date().getFullYear() + 7, new Date().getMonth(), new Date().getDate());

	return {
		resources,
		phoneCodes,
		timeCodes,
		form,
		isValid,
		agentClosedSuccess,
		statusDate,
		messageErrorDate,
		isDisabled,
		popupMenuIndicativeRef,
		popupMenuTimeRef,
		datePickerRef,
		calendarRef,
		newDateToCompare,
		disableDatesFn,
		getInputProps,
		back,
		onDateChange,
		onInvalidDate,
		clearInput,
		handlePressTime,
		handlePressIndicative,
		onPressSendRequest,
	};
};
